import { MapPin, Phone, Mail, MessageSquare, Clock } from 'lucide-react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import LeadForm from '@/components/shared/LeadForm';
import { PLACEHOLDERS } from '@/lib/constants';

function FadeIn({ children, delay = 0 }) {
  const { ref, inView } = useInView({ threshold: 0.15, triggerOnce: true });
  return <motion.div ref={ref} initial={{ opacity: 0, y: 24 }} animate={inView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay, ease: 'easeOut' }}>{children}</motion.div>;
}

export default function ContactPage() {
  return (
    <div className="overflow-hidden">
      {/* HERO */}
      <section className="bg-[#FAF5EF] py-16 lg:py-20">
        <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl">
            <p className="text-[13px] font-medium text-[#E8642C] tracking-[0.2em] uppercase mb-6">Contact</p>
            <h1 className="text-[clamp(2.5rem,5vw,4.5rem)] font-bold text-[#1A1008] leading-[0.95] tracking-[-0.02em] mb-4">
              Let's talk.
            </h1>
            <p className="text-lg text-[#6B5B50] leading-[1.7]">
              Whether you have a specific challenge or just want to explore possibilities.
            </p>
          </motion.div>
        </div>
      </section>

      {/* FORM + DETAILS */}
      <section className="py-20 lg:py-28 bg-white">
        <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <div className="grid lg:grid-cols-12 gap-12 lg:gap-20">
            {/* Left - Form */}
            <div className="lg:col-span-7" data-testid="contact-form">
              <FadeIn>
                <h2 className="text-2xl font-bold text-[#1A1008] mb-8 tracking-tight">Start a Conversation</h2>
                <LeadForm sourceForm="contact-page" />
              </FadeIn>
            </div>

            {/* Right - Details */}
            <div className="lg:col-span-4 lg:col-start-9">
              <FadeIn delay={0.1}>
                <div className="space-y-6">
                  <h3 className="text-[12px] font-medium text-[#6B5B50]/60 tracking-[0.15em] uppercase">Get in Touch</h3>

                  <div className="space-y-3">
                    {[
                      { icon: MapPin, label: 'Office', value: 'Mumbai, India', sublabel: 'Office address placeholder', href: null },
                      { icon: Phone, label: 'Phone', value: '+91 98765 43210', href: 'tel:+919876543210' },
                      { icon: Mail, label: 'Email', value: 'hello@anexiconsulting.com', href: 'mailto:hello@anexiconsulting.com' },
                      { icon: MessageSquare, label: 'WhatsApp', value: 'Chat with us', href: 'https://wa.me/919876543210', color: '#25D366' },
                    ].map(item => {
                      const Icon = item.icon;
                      const Wrapper = item.href ? 'a' : 'div';
                      return (
                        <Wrapper
                          key={item.label}
                          {...(item.href ? { href: item.href, ...(item.label === 'WhatsApp' ? { target: '_blank', rel: 'noopener noreferrer' } : {}) } : {})}
                          className="flex items-start gap-4 p-4 rounded-xl hover:bg-[#FAF5EF] transition-colors group cursor-pointer"
                        >
                          <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${item.color ? '' : 'bg-[#F5D0A9]/20'}`} style={item.color ? { backgroundColor: `${item.color}10` } : {}}>
                            <Icon size={16} className={item.color ? '' : 'text-[#991B1B]'} style={item.color ? { color: item.color } : {}} />
                          </div>
                          <div>
                            <p className="text-[12px] text-[#6B5B50]/60 mb-0.5">{item.label}</p>
                            <p className="text-[14px] font-medium text-[#1A1008] group-hover:text-[#E8642C] transition-colors">{item.value}</p>
                            {item.sublabel && <p className="text-[12px] text-[#6B5B50]/60 mt-0.5">{item.sublabel}</p>}
                          </div>
                        </Wrapper>
                      );
                    })}
                  </div>

                  <div className="flex items-center gap-2 text-[13px] text-[#6B5B50]/60 pt-4">
                    <Clock size={14} />
                    <span>Typically respond within 24 hours</span>
                  </div>

                  {/* Office image */}
                  <div className="mt-6 aspect-[4/3] rounded-2xl overflow-hidden">
                    <img src={PLACEHOLDERS.office} alt="Office" className="w-full h-full object-cover" loading="lazy" />
                  </div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      {/* CALENDLY */}
      <section className="py-20 lg:py-24 bg-[#FAF5EF]">
        <div className="mx-auto w-full max-w-[1400px] px-6 sm:px-8 lg:px-12">
          <FadeIn>
            <div className="text-center max-w-lg mx-auto">
              <h2 className="text-2xl font-bold text-[#1A1008] mb-3 tracking-tight">Prefer to schedule directly?</h2>
              <p className="text-[15px] text-[#6B5B50] mb-8">Book a time that works for you.</p>
              <a
                href="https://calendly.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#1A1008] text-white px-8 py-4 rounded-full text-[14px] font-medium hover:bg-[#3D1C11] transition-colors"
                data-testid="calendly-cta"
              >
                Schedule a Conversation
              </a>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
